import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEK_DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Icons
const BellIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>);
const ChevronRight = ({ size = 16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>);
const CheckIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>);
const PlusIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const HomeIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>);
const ListIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1.5" fill="currentColor" stroke="none"/><circle cx="3" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="3" cy="18" r="1.5" fill="currentColor" stroke="none"/></svg>);
const CalIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const StatsIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>);
const StarIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>);
const LogoutIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>);

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const h = now.getHours(), m = now.getMinutes(), s = now.getSeconds();
  const hDeg = (h%12)*30 + m*0.5, mDeg = m*6 + s*0.1, sDeg = s*6;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg width="42" height="42" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="20" fill="#1C1916" stroke="#2E2924" strokeWidth="1.5"/>
        {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => { const a=(i/12)*Math.PI*2-Math.PI/2,r1=i%3===0?15:16.5; return <line key={i} x1={22+r1*Math.cos(a)} y1={22+r1*Math.sin(a)} x2={22+19*Math.cos(a)} y2={22+19*Math.sin(a)} stroke={i%3===0?'#5A5248':'#2E2924'} strokeWidth={i%3===0?1.5:1}/>; })}
        <line x1="22" y1="22" x2={22+11*Math.cos((hDeg-90)*Math.PI/180)} y2={22+11*Math.sin((hDeg-90)*Math.PI/180)} stroke="#E8E0D5" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="22" y1="22" x2={22+15*Math.cos((mDeg-90)*Math.PI/180)} y2={22+15*Math.sin((mDeg-90)*Math.PI/180)} stroke="#E8E0D5" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="22" y1="22" x2={22+16*Math.cos((sDeg-90)*Math.PI/180)} y2={22+16*Math.sin((sDeg-90)*Math.PI/180)} stroke="#FF5500" strokeWidth="1" strokeLinecap="round"/>
        <circle cx="22" cy="22" r="2" fill="#FF5500"/>
      </svg>
      <div>
        <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 18, fontWeight: 800, color: '#F0EBE3', lineHeight: 1 }}>{String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}</div>
        <div style={{ fontSize: 11, color: '#7A7268', marginTop: 2 }}>{WEEK_DAYS[now.getDay()]}, {now.getDate()} {MONTHS[now.getMonth()]}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [weekStats, setWeekStats] = useState({ sessions: 0, minutes: 0, maxBpm: 0 });
  const [activeNav, setActiveNav] = useState('home');
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const [todayExercises, setTodayExercises] = useState([]);
  const [todaySessions, setTodaySessions] = useState({});
  const [allAgendaExercises, setAllAgendaExercises] = useState([]);
  const [monthEvents, setMonthEvents] = useState([]); // free calendar events
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({ title:'', description:'', category:'rehearsal', date:'', time:'' });
  const [savingEvent, setSavingEvent] = useState(false);
  const [monthSessions, setMonthSessions] = useState({}); // { 'YYYY-MM-DD': { ex_id: seconds } }
  const [selectedDay, setSelectedDay] = useState(null);
  const [freshSessions, setFreshSessions] = useState({});
  const [calendarDate, setCalendarDate] = useState(new Date()); // currently viewed month
  const [agendaPrograms, setAgendaPrograms] = useState([]); // programs with start_date and recurrence

  useEffect(() => {
    loadData();
    const handleFocus = () => loadData();
    const handleVisibility = () => { if (!document.hidden) loadData(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const normalizeProgExs = (progExs) => progExs.map(pe => {
    if (!pe.exercises && pe.user_exercises) {
      const ue = pe.user_exercises;
      return {
        ...pe,
        exercises: {
          id: 'user-' + ue.id,
          title: ue.title,
          image_url: ue.image_url,
          default_bpm: ue.default_bpm,
          categories: ue.subcategories?.categories || null,
          _isUserExercise: true,
          _userExerciseId: ue.id,
          _defaultTimeSig: ue.default_time_sig,
        }
      };
    }
    return pe;
  });

  const loadData = async () => {
    setLoading(true);

    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const today = new Date(); today.setHours(0,0,0,0);
    const jsDay = new Date().getDay();
    const ourDay = jsDay === 0 ? 6 : jsDay - 1;
    const viewYear = calendarDate.getFullYear();
    const viewMonth = calendarDate.getMonth();
    const monthStart = new Date(viewYear, viewMonth, 1);
    const monthEnd = new Date(viewYear, viewMonth + 1, 1);

    // Run all independent queries in parallel
    const [
      { data: sessions },
      { data: agendaProgs },
      { data: evts },
      { data: mSess },
      { data: todaySess },
    ] = await Promise.all([
      supabase.from('sessions').select('duration_seconds, bpm').eq('user_id', user.id).gte('completed_at', weekAgo.toISOString()),
      supabase.from('programs').select('id, start_date, recurrence, weeks').eq('user_id', user.id).eq('mode', 'agenda'),
      supabase.from('events').select('*').eq('user_id', user.id),
      supabase.from('sessions').select('exercise_id, duration_seconds, completed_at').eq('user_id', user.id).gte('completed_at', monthStart.toISOString()).lt('completed_at', monthEnd.toISOString()),
      supabase.from('sessions').select('exercise_id, user_exercise_id, duration_seconds').eq('user_id', user.id).gte('completed_at', today.toISOString()),
    ]);

    // Process week stats
    if (sessions && sessions.length > 0) {
      setWeekStats({
        sessions: sessions.length,
        minutes: Math.round(sessions.reduce((a, s) => a + (s.duration_seconds || 0), 0) / 60),
        maxBpm: Math.max(...sessions.map(s => s.bpm || 0)),
      });
    }

    // Process events
    if (evts) setMonthEvents(evts.map(e => ({ ...e, date: String(e.date).split('T')[0] })));

    // Process month sessions for calendar dots
    if (mSess) {
      const byDay = {};
      mSess.forEach(s => {
        const d = s.completed_at.split('T')[0];
        if (!byDay[d]) byDay[d] = {};
        byDay[d][s.exercise_id] = (byDay[d][s.exercise_id] || 0) + (s.duration_seconds || 0);
      });
      setMonthSessions(byDay);
    }

    // Process today sessions
    if (todaySess) {
      const totals = {};
      todaySess.forEach(s => {
        const dur = s.duration_seconds || 0;
        if (s.exercise_id) {
          totals[s.exercise_id] = (totals[s.exercise_id] || 0) + dur;
          totals[String(s.exercise_id)] = (totals[String(s.exercise_id)] || 0) + dur;
        }
        if (s.user_exercise_id) {
          totals['user-' + s.user_exercise_id] = (totals['user-' + s.user_exercise_id] || 0) + dur;
        }
      });
      setTodaySessions(totals);
    }

    // Process agenda programs — these depend on agendaProgs so run after
    if (agendaProgs) {
      setAgendaPrograms(agendaProgs);
      if (agendaProgs.length > 0) {
        const progIds = agendaProgs.map(p => p.id);
        const selectStr = '*, exercises(id, title, image_url, default_bpm, categories(name,color)), user_exercises(id, title, image_url, default_bpm, default_time_sig, subcategories(name, categories(name,color))), program_id';
        const [{ data: allAgenda }, { data: progExs }] = await Promise.all([
          supabase.from('program_exercises').select(selectStr).in('program_id', progIds).not('day_of_week', 'is', null),
          supabase.from('program_exercises').select(selectStr.replace(', program_id','')).in('program_id', progIds).eq('day_of_week', ourDay),
        ]);
        if (allAgenda) setAllAgendaExercises(normalizeProgExs(allAgenda));
        if (progExs) setTodayExercises(normalizeProgExs(progExs));
      }
    }

    setLoading(false);
  };

  // Check if a program exercise should appear on a specific date
  const isActiveOnDate = (pe, dateObj) => {
    const prog = agendaPrograms.find(p => p.id === pe.program_id);
    if (!prog) return true;

    const startDate = prog.start_date ? new Date(prog.start_date + 'T00:00:00') : null;
    if (!startDate) return true;

    // Never show before program start date
    if (dateObj < startDate) return false;

    if (prog.recurrence === 'weekly' && prog.weeks) {
      // Show for X weeks from start date
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (parseInt(prog.weeks) || 4) * 7);
      if (dateObj > endDate) return false;
    } else if (prog.recurrence === 'monthly') {
      // Show only within the same month as start date
      if (dateObj.getFullYear() !== startDate.getFullYear() ||
          dateObj.getMonth() !== startDate.getMonth()) return false;
    }
    // 'forever' — show indefinitely after start date
    return true;
  };

  const openDayModal = async (day, dateObj, exercises) => {
    const jsDay = dateObj.getDay();
    const ourDay = jsDay === 0 ? 6 : jsDay - 1;
    const isToday = dateObj.toDateString() === new Date().toDateString();

    // Filter exercises for this specific day
    const dayExercises = exercises.filter(pe => pe.day_of_week === ourDay);

    let sessionTotals = {};

    if (isToday) {
      // Use todaySessions — already has both number and string keys
      sessionTotals = todaySessions;
    } else {
      const dayStart = new Date(dateObj); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dateObj); dayEnd.setHours(23,59,59,999);
      const { data: sess } = await supabase.from('sessions')
        .select('exercise_id, duration_seconds')
        .eq('user_id', user.id)
        .gte('completed_at', dayStart.toISOString())
        .lte('completed_at', dayEnd.toISOString());
      if (sess) {
        sess.forEach(s => {
          const id = s.exercise_id;
          const dur = s.duration_seconds || 0;
          sessionTotals[id] = (sessionTotals[id] || 0) + dur;
          sessionTotals[String(id)] = (sessionTotals[String(id)] || 0) + dur;
        });
      }
    }

    setFreshSessions(sessionTotals);
    setSelectedDay({ day, date: dateObj, exercises: dayExercises, freshSessions: sessionTotals });
  };

  // Event category config
  const EVENT_CATS = {
    rehearsal: { label:'Rehearsal', color:'#00B4D8', shape:'circle' },
    concert:   { label:'Concert', color:'#FFD700', shape:'star' },
    study:     { label:'Study', color:'#7B2FBE', shape:'triangle' },
    other:     { label:'Other', color:'#EC4899', shape:'diamond' },
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.date) return;
    setSavingEvent(true);
    await supabase.from('events').insert({
      user_id: user.id, title: eventForm.title, description: eventForm.description,
      category: eventForm.category, date: eventForm.date, time: eventForm.time || null,
    });
    setSavingEvent(false);
    setShowEventForm(false);
    setEventForm({ title:'', description:'', category:'rehearsal', date:'', time:'' });
    loadData();
    // Refresh day modal if open
    if (selectedDay) {
      const d = selectedDay.date;
      openDayModal(selectedDay.day, d, selectedDay.exercises);
    }
  };

  const deleteEvent = async (id) => {
    await supabase.from('events').delete().eq('id', id);
    loadData();
    if (selectedDay) openDayModal(selectedDay.day, selectedDay.date, selectedDay.exercises);
  };

  const EventShape = ({ category, size=7 }) => {
    const cat = EVENT_CATS[category] || EVENT_CATS.other;
    const c = cat.color;
    if (cat.shape === 'circle') return <div style={{ width:size, height:size, borderRadius:'50%', background:c }}/>;
    if (cat.shape === 'star') return (
      <svg width={size} height={size} viewBox="0 0 10 10">
        <polygon points="5,0 6.5,3.5 10,3.5 7.5,6 8.5,10 5,7.5 1.5,10 2.5,6 0,3.5 3.5,3.5" fill={c}/>
      </svg>
    );
    if (cat.shape === 'triangle') return (
      <svg width={size} height={size} viewBox="0 0 10 10">
        <polygon points="5,0 10,10 0,10" fill={c}/>
      </svg>
    );
    if (cat.shape === 'diamond') return (
      <svg width={size} height={size} viewBox="0 0 10 10">
        <polygon points="5,0 10,5 5,10 0,5" fill={c}/>
      </svg>
    );
    return null;
  };

  const now = new Date();
  const firstName = profile?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Músico';

  const prevMonth = () => setCalendarDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCalendarDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  // When calendarDate changes, reload sessions for that month
  useEffect(() => { loadData(); }, [calendarDate]);

  return (
    <div style={{ background: '#111009', minHeight: '100vh', maxWidth: 'var(--app-width, 860px)', margin: '0 auto', paddingBottom: 80, position: 'relative' }}>
      <div className="bg-grid"/>

      {/* Top bar */}
      <div style={{ padding: '52px 20px 16px', background: 'linear-gradient(180deg, #0A0907 0%, #111009 100%)', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => navigate('/profile')}>
              {profile?.photo_url
                ? <img src={profile.photo_url} alt="" style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', border: '2px solid #FF5500' }}/>
                : <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#FF5500', border: '2px solid #FF5500', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed'", fontSize: 20, fontWeight: 800, color: '#fff' }}>{firstName[0].toUpperCase()}</div>
              }
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: '#22C55E', border: '2px solid #0A0907' }}/>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#7A7268', fontWeight: 500 }}>Hello,</div>
              <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 20, fontWeight: 800, color: '#F0EBE3', lineHeight: 1 }}>{firstName}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LiveClock/>
            <button onClick={signOut} style={{ background: '#1C1916', border: '1px solid #2A2520', color: '#7A7268', width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Sair">
              <LogoutIcon/>
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: '8px 16px 20px', position: 'relative', zIndex: 1 }}>

        {/* Metronome shortcut */}
        <div className="fu" style={{ marginBottom: 16 }}>
          <button onClick={() => navigate('/metronome')} style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2A2520', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, cursor:'pointer', transition:'all 0.18s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='#FF5500'}
            onMouseLeave={e => e.currentTarget.style.borderColor='#2A2520'}>
            <div style={{ width:42, height:42, borderRadius:10, background:'rgba(255,85,0,0.12)', border:'1.5px solid rgba(255,85,0,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF5500" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            </div>
            <div style={{ flex:1, textAlign:'left' }}>
              <div style={{ fontFamily:"'Barlow Condensed'", fontSize:16, fontWeight:800, color:'#F0EBE3', letterSpacing:1 }}>METRONOME</div>
              <div style={{ fontSize:11, color:'#5A5248', marginTop:2 }}>Quick access · Tap tempo · Rhythm Trainer</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3A3530" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        {/* PC: 2-column layout wrapper */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:16 }} className="dashboard-grid">

        {/* Today's Training */}
        <div className="fu fu2" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="section-title">Today's Practice</div>
            <button onClick={() => navigate('/programs')} style={{ background: 'transparent', color: '#FF5500', fontSize: 12, fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1 }}>
              PROGRAMS
            </button>
          </div>

          {todayExercises.length === 0 ? (
            <div onClick={() => navigate('/programs')} style={{ background: '#1C1916', border: '1.5px dashed #2A2520', borderRadius: 12, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
              <div style={{ fontSize: 28 }}>🎯</div>
              <div>
                <div style={{ fontSize: 13, color: '#5A5248', fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1 }}>NO TRAINING SCHEDULED</div>
                <div style={{ fontSize: 12, color: '#3A3530', marginTop: 3 }}>Go to programs to organize your training</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {todayExercises.map(pe => {
                const ex = pe.exercises;
                if (!ex) return null;
                const trainedSeconds = (Object.keys(freshSessions).length > 0 ? freshSessions : todaySessions)[ex.id] || 0;
                const requiredSeconds = (pe.duration_minutes || 10) * 60;
                const done = trainedSeconds >= requiredSeconds;
                const progress = Math.min(1, trainedSeconds / requiredSeconds);
                const color = ex.categories?.color || '#FF5500';
                return (
                  <div key={pe.id} onClick={() => navigate(ex._isUserExercise ? '/exercise/user-' + ex._userExerciseId : '/exercise/' + ex.id, { state: ex._isUserExercise ? { userExercise: true, defaultTimeSig: ex._defaultTimeSig, programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } : { programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } })}
                    style={{ background: done ? '#0F2318' : '#1C1916', border: done ? '1.5px solid #22C55E33' : '1.5px solid #2A2520', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.18s' }}>
                    {/* Status indicator */}
                    <div style={{ flexShrink: 0 }}>
                      {done ? (
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                        </div>
                      ) : (
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: '#FF3B30', opacity: 0.9 }}/>
                      )}
                    </div>
                    {/* Image */}
                    {ex.image_url
                      ? <img src={ex.image_url} alt="" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 8, flexShrink: 0, opacity: done ? 0.6 : 1 }}/>
                      : <div style={{ width: 42, height: 42, background: color + '18', borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🥁</div>
                    }
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: done ? '#86EFAC' : '#F0EBE3', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ex.title}</div>
                      <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                        <span style={{ color: done ? '#4ADE80' : '#FF5500', fontFamily: "'Barlow Condensed'", fontWeight: 700 }}>⏱ {pe.duration_minutes}min</span>
                        {pe.target_bpm && <span style={{ color: '#7A7268', fontFamily: "'Barlow Condensed'", fontWeight: 700 }}>♩ {pe.target_bpm}</span>}
                      </div>
                    </div>
                    {done && <span style={{ fontSize: 10, color: '#22C55E', fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1, flexShrink: 0 }}>DONE</span>}
                  </div>
                );
              })}
              {/* Progress summary */}
              {(() => {
                const doneCount = todayExercises.filter(pe => {
                  const trained = todaySessions[pe.exercises?.id] || 0;
                  const required = (pe.duration_minutes || 10) * 60;
                  return trained >= required;
                }).length;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 2px' }}>
                    <div style={{ flex: 1, height: 4, background: '#1C1916', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(doneCount / todayExercises.length) * 100}%`, background: '#22C55E', borderRadius: 2, transition: 'width 0.5s' }}/>
                    </div>
                    <span style={{ fontSize: 11, color: '#7A7268', fontFamily: "'Barlow Condensed'", fontWeight: 700, flexShrink: 0 }}>
                      {doneCount}/{todayExercises.length} DONE
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Smart Calendar */}
        <div className="fu fu3" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="section-title">Schedule</div>
            <button onClick={() => navigate('/programs')} style={{ background: 'transparent', color: '#FF5500', fontSize: 12, fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
              PROGRAMS <ChevronRight/>
            </button>
          </div>
          <div style={{ background: '#1C1916', border: '1px solid #2A2520', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 17, fontWeight: 800, color: '#F0EBE3', letterSpacing: 1 }}>
                {MONTHS[calendarDate.getMonth()]} {calendarDate.getFullYear()}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={prevMonth} style={{ background: '#2A2520', color: '#9A9088', width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'scaleX(-1)' }}>
                  <ChevronRight size={14}/>
                </button>
                {calendarDate.getMonth() !== now.getMonth() || calendarDate.getFullYear() !== now.getFullYear() ? (
                  <button onClick={() => setCalendarDate(new Date())} style={{ background: '#FF550022', color: '#FF5500', height: 28, padding: '0 8px', borderRadius: 6, border: '1px solid #FF550044', cursor: 'pointer', fontFamily: "'Barlow Condensed'", fontWeight: 700, fontSize: 11, letterSpacing: 1 }}>
                    HOJE
                  </button>
                ) : null}
                <button onClick={nextMonth} style={{ background: '#2A2520', color: '#9A9088', width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight size={14}/>
                </button>
              </div>
            </div>
            <button onClick={() => { const today = new Date(); setEventForm(f => ({ ...f, date: `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}` })); setShowEventForm(true); }}
              style={{ width:'100%', padding:'8px', background:'rgba(255,85,0,0.08)', border:'1.5px dashed rgba(255,85,0,0.4)', borderRadius:8, color:'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:12, letterSpacing:1, cursor:'pointer', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              + ADD EVENT
            </button>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 8 }}>
              {WEEK_DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#4A4640', fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>{d}</div>)}
            </div>
            {(() => {
              const firstDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1).getDay();
              const daysInMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth()+1, 0).getDate();
              const cells = [...Array(firstDay).fill(null), ...Array.from({length: daysInMonth}, (_,i) => i+1)];
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px 2px' }}>
                  {cells.map((day, i) => {
                    if (!day) return <div key={i}/>;
                    const dateObj = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
                    const isToday = dateObj.toDateString() === now.toDateString();
                    const isPast = dateObj < new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const jsDay = dateObj.getDay();
                    const ourDay = jsDay === 0 ? 6 : jsDay - 1;
                    // Exercises scheduled for this day
                    const dayExs = allAgendaExercises.filter(pe => pe.day_of_week === ourDay && isActiveOnDate(pe, dateObj));
                    const hasTraining = dayExs.length > 0;
                    // Check if done (past or today only)
                    let dotColor = null;
                    if (hasTraining) {
                      if (!isPast && !isToday) {
                        dotColor = '#FF5500'; // future — orange
                      } else {
                        // For today use todaySessions (already loaded), for past use monthSessions
                        let daySess = {};
                        if (isToday) {
                          // todaySessions is { exercise_id: total_seconds }
                          daySess = todaySessions;
                        } else {
                          const dateStr = dateObj.toISOString().split('T')[0];
                          daySess = monthSessions[dateStr] || {};
                        }
                        const allDone = dayExs.every(pe => {
                          const trained = daySess[pe.exercises?.id] || 0;
                          return trained >= (pe.duration_minutes || 10) * 60;
                        });
                        const anyDone = dayExs.some(pe => {
                          const trained = daySess[pe.exercises?.id] || 0;
                          return trained > 0;
                        });
                        dotColor = allDone ? '#22C55E' : anyDone ? '#F9A825' : '#FF3B30';
                      }
                    }
                    const y2 = dateObj.getFullYear(), m2 = String(dateObj.getMonth()+1).padStart(2,'0'), d2 = String(dateObj.getDate()).padStart(2,'0');
                    const dateStr2 = `${y2}-${m2}-${d2}`;
                    const hasEvents = monthEvents.some(e => String(e.date).split('T')[0] === dateStr2);
                    const hasAnything = hasTraining || hasEvents;

                    return (
                      <div key={i} onClick={() => { if (hasAnything) openDayModal(day, dateObj, dayExs); }}
                        style={{ textAlign: 'center', padding: '5px 2px', borderRadius: 6, background: isToday ? '#FF5500' : 'transparent', cursor: hasAnything ? 'pointer' : 'default' }}>
                        <span style={{ fontSize: 12, fontWeight: isToday ? 800 : 400, color: isToday ? '#fff' : '#C4B9AC' }}>{day}</span>
                        {dotColor && (
                          <div style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, margin: '2px auto 0' }}/>
                        )}
                        {/* Event shapes */}
                        {(() => {
                          const dayEvts = monthEvents.filter(e => String(e.date).split('T')[0] === dateStr2);
                          if (dayEvts.length === 0) return null;
                          const cat = EVENT_CATS[dayEvts[0].category] || EVENT_CATS.other;
                          const c = cat.color;
                          if (cat.shape === 'star') return <svg width="6" height="6" viewBox="0 0 10 10" style={{ display:'block', margin:'1px auto 0' }}><polygon points="5,0 6.5,3.5 10,3.5 7.5,6 8.5,10 5,7.5 1.5,10 2.5,6 0,3.5 3.5,3.5" fill={c}/></svg>;
                          if (cat.shape === 'triangle') return <svg width="6" height="6" viewBox="0 0 10 10" style={{ display:'block', margin:'1px auto 0' }}><polygon points="5,0 10,10 0,10" fill={c}/></svg>;
                          if (cat.shape === 'diamond') return <svg width="6" height="6" viewBox="0 0 10 10" style={{ display:'block', margin:'1px auto 0' }}><polygon points="5,0 10,5 5,10 0,5" fill={c}/></svg>;
                          return <div style={{ width:6, height:6, borderRadius:'50%', background:c, margin:'1px auto 0' }}/>;
                        })()}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* Legend */}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 12, borderTop: '1px solid #2A2520', flexWrap:'wrap' }}>
              {[['#22C55E','Complete'],['#F9A825','Partial'],['#FF3B30','To do'],['#FF5500','Scheduled']].map(([c,l]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0 }}/>
                  <span style={{ fontSize: 10, color: '#5A5248', fontFamily: "'Barlow Condensed'", fontWeight: 600 }}>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        </div>{/* end dashboard-grid */}

        {/* Week stats */}
        <div className="fu fu4">
          <div className="section-title">This Week</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(var(--cat-cols, 3), 1fr)', gap: 8 }}>
            {[
              { label: 'Sessions', value: weekStats.sessions, icon: '🥁' },
              { label: 'Minutes', value: weekStats.minutes, icon: '⏱' },
              { label: 'Max BPM', value: weekStats.maxBpm || '—', icon: '⚡' },
            ].map(s => (
              <div key={s.label} style={{ background: '#1C1916', border: '1px solid #2A2520', borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 26, fontWeight: 800, color: '#FF5500', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#5A5248', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Form Modal */}
      {showEventForm && (
        <div style={{ position:'fixed', inset:0, zIndex:300, background:'rgba(0,0,0,0.88)', display:'flex', alignItems:'flex-end', justifyContent:'center', backdropFilter:'blur(6px)' }}>
          <div style={{ background:'#171410', borderRadius:'20px 20px 0 0', padding:'28px 20px 44px', width:'100%', maxWidth:'var(--app-width, 860px)' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:20, fontWeight:800, letterSpacing:2, color:'#F0EBE3', marginBottom:20 }}>NEW EVENT</div>
            
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1.5, display:'block', marginBottom:6 }}>TITLE *</label>
              <input style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' }}
                value={eventForm.title} onChange={e => setEventForm(f=>({...f,title:e.target.value}))} placeholder="Ex: Concerto no Bar, Ensaio..."/>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1.5, display:'block', marginBottom:6 }}>CATEGORY</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {Object.entries(EVENT_CATS).map(([key, cat]) => (
                  <button key={key} onClick={() => setEventForm(f=>({...f,category:key}))}
                    style={{ padding:'7px 12px', borderRadius:8, background: eventForm.category===key?(cat.color+'22'):'#1C1916', border:`1.5px solid ${eventForm.category===key?cat.color:'#2A2520'}`, color: eventForm.category===key?cat.color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                    <EventShape category={key} size={10}/> {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              <div>
                <label style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1.5, display:'block', marginBottom:6 }}>DATE</label>
                <input type="date" style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' }}
                  value={eventForm.date} onChange={e => setEventForm(f=>({...f,date:e.target.value}))}/>
              </div>
              <div>
                <label style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1.5, display:'block', marginBottom:6 }}>TIME (optional)</label>
                <input type="time" style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' }}
                  value={eventForm.time} onChange={e => setEventForm(f=>({...f,time:e.target.value}))}/>
              </div>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1.5, display:'block', marginBottom:6 }}>NOTES</label>
              <input style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' }}
                value={eventForm.description} onChange={e => setEventForm(f=>({...f,description:e.target.value}))} placeholder="Optional description..."/>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowEventForm(false)} style={{ flex:1, padding:'13px', background:'#2A2520', color:'#9A9088', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14, letterSpacing:1, border:'none', cursor:'pointer' }}>CANCEL</button>
              <button onClick={saveEvent} disabled={!eventForm.title.trim() || !eventForm.date || savingEvent}
                style={{ flex:2, padding:'13px', background:'#FF5500', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:14, letterSpacing:2, border:'none', cursor:'pointer' }}>
                {savingEvent ? 'SAVING…' : 'SAVE EVENT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Modal */}
      {selectedDay && (
        <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'flex-end', justifyContent:'center', backdropFilter:'blur(6px)' }}
          onClick={e => e.target === e.currentTarget && setSelectedDay(null)}>
          <div style={{ background:'#171410', borderRadius:'20px 20px 0 0', padding:'24px 20px 40px', width:'100%', maxWidth:'var(--app-width, 860px)', maxHeight:'80vh', overflowY:'auto', marginLeft:'auto', marginRight:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize:11, fontWeight:700, letterSpacing:2, color:'#FF5500', marginBottom:3 }}>DAY'S TRAINING</div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize:22, fontWeight:800, color:'#F0EBE3' }}>
                  {selectedDay.date.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' }).toUpperCase()}
                </div>
              </div>
              <button onClick={() => setSelectedDay(null)} style={{ background:'#2A2520', color:'#9A9088', width:36, height:36, borderRadius:'50%', border:'none', cursor:'pointer', fontSize:18 }}>✕</button>
            </div>
            {/* Events for this day */}
            {(() => {
              const d = selectedDay.date;
              const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const dayEvts = monthEvents.filter(e => String(e.date).split('T')[0] === dateStr);
              return dayEvts.map(evt => {
                const cat = EVENT_CATS[evt.category] || EVENT_CATS.other;
                return (
                  <div key={evt.id} style={{ background:'#1C1916', border:`1.5px solid ${cat.color}44`, borderRadius:12, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                    <div style={{ width:28, height:28, borderRadius:6, background:cat.color+'22', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <EventShape category={evt.category} size={14}/>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3' }}>{evt.title}</div>
                      <div style={{ display:'flex', gap:8, fontSize:11, marginTop:2 }}>
                        <span style={{ color:cat.color, fontFamily:"'Barlow Condensed'", fontWeight:700 }}>{cat.label}</span>
                        {evt.time && <span style={{ color:'#5A5248' }}>⏰ {evt.time}</span>}
                      </div>
                      {evt.description && <div style={{ fontSize:11, color:'#5A5248', marginTop:3 }}>{evt.description}</div>}
                    </div>
                    <button onClick={() => deleteEvent(evt.id)} style={{ background:'#2A1010', color:'#FF6666', width:28, height:28, borderRadius:7, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:13 }}>✕</button>
                  </div>
                );
              });
            })()}

            {/* Add event button */}
            <button onClick={() => { setEventForm(f => ({ ...f, date: `${selectedDay.date.getFullYear()}-${String(selectedDay.date.getMonth()+1).padStart(2,'0')}-${String(selectedDay.date.getDate()).padStart(2,'0')}` })); setShowEventForm(true); }}
              style={{ width:'100%', padding:'10px', background:'transparent', border:'1.5px dashed #2A2520', borderRadius:10, color:'#5A5248', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:12, letterSpacing:1, cursor:'pointer', marginBottom:12 }}>
              + ADD EVENT
            </button>

            {selectedDay.exercises.map(pe => {
              const ex = pe.exercises;
              if (!ex) return null;
              const fs = selectedDay.freshSessions || {};
              const exId = ex.id;
              const trained = fs[exId] || fs[String(exId)] || 0;
              const required = (pe.duration_minutes || 10) * 60;
              const done = trained >= required;
              const color = ex.categories?.color || '#FF5500';
              return (
                <div key={pe.id} onClick={() => { setSelectedDay(null); navigate(ex._isUserExercise ? '/exercise/user-' + ex._userExerciseId : '/exercise/' + ex.id, { state: ex._isUserExercise ? { userExercise: true, defaultTimeSig: ex._defaultTimeSig, programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } : { programSettings: { duration_minutes: pe.duration_minutes, target_bpm: pe.target_bpm } } }); }}
                  style={{ background: done?'#0F2318':'#1C1916', border: done?'1.5px solid #22C55E33':'1px solid #2A2520', borderRadius:12, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, marginBottom:8, cursor:'pointer' }}>
                  <div style={{ flexShrink:0 }}>
                    {done
                      ? <div style={{ width:28, height:28, borderRadius:'50%', background:'#22C55E', display:'flex', alignItems:'center', justifyContent:'center' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg></div>
                      : <div style={{ width:28, height:28, borderRadius:6, background:'#FF3B30', opacity:0.9 }}/>
                    }
                  </div>
                  {ex.image_url
                    ? <img src={ex.image_url} alt="" style={{ width:44, height:44, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, flexShrink:0, opacity:done?0.6:1 }}/>
                    : <div style={{ width:44, height:44, background:color+'18', borderRadius:8, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>🥁</div>
                  }
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:done?'#86EFAC':'#F0EBE3', marginBottom:3 }}>{ex.title}</div>
                    <div style={{ display:'flex', gap:8, fontSize:11 }}>
                      <span style={{ color:done?'#4ADE80':'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>⏱ {pe.duration_minutes}min</span>
                      {pe.target_bpm && <span style={{ color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>♩ {pe.target_bpm}</span>}
                    </div>
                  </div>
                  {done && <span style={{ fontSize:10, color:'#22C55E', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, flexShrink:0 }}>DONE</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom nav */}
      {!isDesktop && (<div className="bottom-nav" style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 'var(--app-width, 860px)', background: '#0E0C0A', borderTop: '1px solid #1E1C18', padding: '10px 0 20px', display: 'flex', justifyContent: 'space-around', zIndex: 100 }}>
        {[
          { id: 'home', icon: <HomeIcon/>, label: 'Home', path: '/' },
          { id: 'exercises', icon: <ListIcon/>, label: 'Exercises', path: '/exercises' },
          { id: 'myexercises', icon: <StarIcon/>, label: 'My Studies', path: '/my-exercises' },
          { id: 'stats', icon: <StatsIcon/>, label: 'Stats', path: '/stats' },
        ].map(item => (
          <button key={item.id} onClick={() => { setActiveNav(item.id); if (item.path !== '/') navigate(item.path); }} style={{ background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: activeNav === item.id ? '#FF5500' : '#4A4640' }}>
            {item.icon}
            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>{item.label.toUpperCase()}</span>
            {activeNav === item.id && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#FF5500', marginTop: -2 }}/>}
          </button>
        ))}
      </div>)}
    </div>
  );
}
